import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Popover } from "@mui/material";
import { createEditor, Descendant, Editor, Range, Transforms } from "slate";
import { withHistory } from "slate-history";
import { Editable, ReactEditor, Slate, withReact } from "slate-react";
import { UserModel } from "../../models/users.model";
import { UserService } from "../../services/UserService";
import Config from "../../config";
import Avatar from "../shared/avatar.component";
import {
    computeLength,
    mapDescendantsToText,
    mapTextToDescendants,
    MentionElement,
} from "../shared/mentions/models";

type TagCategory = {
    catagory: string;
    label: string;
    tags: { id: string; display: string; length: number; className: string }[];
};

export type InternalNoteComposerEditorProps = {
    body: string;
    emoji: string;
    onChange: (event: { target: { value: string } }, length: number) => void;
    onKeyDown: (event: React.KeyboardEvent) => void;
    placeholder: string;
    maxLength?: number;
    users: UserModel[];
    readOnly?: boolean;
    lineHeight?: number;
    IsLinkAllowed?: boolean;
    composerAnchorRef?: React.RefObject<HTMLElement | null>;
};

const EMPTY_VALUE: Descendant[] = [{ type: "paragraph", children: [{ text: "" }] } as any];

const teamUserMentionId = (userId: string) => `u${(userId || "").replace(/-/g, "")}`;

const normalizeUserId = (id?: string | null) => (id || "").trim().toLowerCase().replace(/-/g, "");

const isSameUserId = (a?: string | null, b?: string | null) => {
    const first = normalizeUserId(a);
    const second = normalizeUserId(b);
    return !!first && first === second;
};

const slashDisplayName = (u: UserModel) =>
    `${u.FirstName || ""} ${u.LastName || ""}`.trim() || u.Email || "User";

const withMentions = (editor: any) => {
    const { isInline, isVoid } = editor;

    editor.isInline = (element: any) =>
        ["mention", "link", "mail"].includes(element.type) || isInline(element);

    editor.isVoid = (element: any) =>
        ["mention", "link", "mail"].includes(element.type) || element.id != null || isVoid(element);

    return editor;
};

const insertMention = (editor: any, character: string, length: number) => {
    const mention: MentionElement = {
        type: "mention",
        character,
        length,
        children: [{ text: "" }],
        className: "",
    };

    Transforms.insertNodes(editor, mention as any);
    Transforms.move(editor);
};

const TeamMentionChip = ({ attributes, children, element, tags }: any) => {
    const allTags = tags.flatMap((c: TagCategory) => c.tags);
    const mentionId = (element.character || "").replaceAll(" ", "");

    const tag = allTags.find((x: any) => (x.id || "").replaceAll(" ", "") === mentionId);

    return (
        <span
            {...attributes}
            contentEditable={false}
            data-cy={`mention-${element.character.replace(" ", "-")}`}
            className={`tag ${tag?.className ?? "internal-note-team-user"} bg-color-internal-note-composer-mention-chip-bg color-internal-note-text`}
        >
            {tag?.display || element.character}
            {children}
        </span>
    );
};

const NoteElement = (props: any) => {
    const { attributes, children, element, tags, spellCheck } = props;

    if (element.type === "mention") {
        return <TeamMentionChip {...props} tags={tags} />;
    }

    if (element.type === "link") {
        const hasProtocol = /^https?:\/\//i.test(element.character);
        const url = hasProtocol ? element.character : `http://${element.character}`;

        return (
            <a
                href={url}
                className="color-text-field-focused t-decoration-underline"
                target="_blank"
                rel="noopener noreferrer"
                style={{ cursor: "pointer" }}
            >
                {element.character}
            </a>
        );
    }

    if (element.type === "mail") {
        return (
            <a
                href={`mailto:${element.character}`}
                className="color-text-field-focused t-decoration-underline"
                target="_blank"
                rel="noopener noreferrer"
                style={{ cursor: "pointer" }}
            >
                {element.character}
            </a>
        );
    }

    return (
        <p className="my-auto" {...attributes} spellCheck={!!spellCheck}>
            {children}
        </p>
    );
};

export default function InternalNoteComposerEditor(props: InternalNoteComposerEditorProps) {
    const editorRef = useRef<HTMLDivElement | null>(null);

    const [slashTarget, setSlashTarget] = useState<Range | null>(null);
    const [slashSearch, setSlashSearch] = useState("");
    const [highlightIndex, setHighlightIndex] = useState(-1);

    const editor = useMemo(() => withMentions(withReact(withHistory(createEditor() as any))), []);

    const mentionableUsers = useMemo(() => {
        const currentUserId = UserService.getCurrentUserFromLocalStorage()?.ID;

        return (props.users || []).filter((u) => {
            if (u.IsDeleted) return false;
            return currentUserId ? !isSameUserId(u.ID, currentUserId) : true;
        });
    }, [props.users]);

    const editorTags = useMemo<TagCategory[]>(() => {
        const tags = mentionableUsers.map((u) => {
            const display = slashDisplayName(u);

            return {
                id: teamUserMentionId(u.ID),
                display,
                length: Math.max(display.length, 1),
                className: "internal-note-team-user br-3",
            };
        });

        return [{ catagory: "Team", label: "Team", tags }];
    }, [mentionableUsers]);

    const allTags = useMemo(() => editorTags.flatMap((c) => c.tags), [editorTags]);

    const filteredUsers = useMemo(() => {
        if (!slashTarget) return [];

        const search = slashSearch.toLowerCase();

        return mentionableUsers.filter((u) => {
            const name = slashDisplayName(u).toLowerCase();
            const email = (u.Email || "").toLowerCase();

            return !search || name.includes(search) || email.includes(search);
        });
    }, [slashTarget, slashSearch, mentionableUsers]);

    const closeSlashMenu = useCallback(() => {
        setSlashTarget(null);
        setSlashSearch("");
        setHighlightIndex(-1);
    }, []);

    const renderElement = useCallback(
        (innerProps: any) => <NoteElement {...innerProps} tags={editorTags} spellCheck />,
        [editorTags]
    );

    const resetEditorValue = useCallback(
        (nextValue: Descendant[]) => {
            editor.children = nextValue;
            Transforms.select(editor, Editor.end(editor, []));
        },
        [editor]
    );

    useEffect(() => {
        if (props.emoji) {
            Editor.insertText(editor, props.emoji);
        }
    }, [props.emoji, editor]);

    useEffect(() => {
        const currentText = mapDescendantsToText(editor.children as Descendant[]);

        if (currentText === props.body) return;

        const nextValue = props.body
            ? mapTextToDescendants(props.body, allTags, !!props.IsLinkAllowed)
            : EMPTY_VALUE;

        resetEditorValue(nextValue);
    }, [props.body, allTags, props.IsLinkAllowed, editor, resetEditorValue]);

    const updateSlashMenu = useCallback(() => {
        const { selection } = editor;

        if (!selection || !Range.isCollapsed(selection) || !mentionableUsers.length) {
            closeSlashMenu();
            return;
        }

        const [start] = Range.edges(selection);
        const beforeText = Editor.string(editor, {
            anchor: { path: start.path, offset: 0 },
            focus: start,
        });

        const slashIndex = beforeText.lastIndexOf("/");

        if (slashIndex === -1) {
            closeSlashMenu();
            return;
        }

        const search = beforeText.slice(slashIndex + 1);

        if (/[\s\n]/.test(search)) {
            closeSlashMenu();
            return;
        }

        const hasMatch = mentionableUsers.some((u) => {
            const query = search.toLowerCase();
            const name = slashDisplayName(u).toLowerCase();
            const email = (u.Email || "").toLowerCase();

            return !query || name.includes(query) || email.includes(query);
        });

        if (!hasMatch) {
            closeSlashMenu();
            return;
        }

        setSlashTarget(selection);
        setSlashSearch(search);
        setHighlightIndex(-1);
    }, [editor, mentionableUsers, closeSlashMenu]);

    const selectUser = useCallback(
        (user: UserModel) => {
            const id = teamUserMentionId(user.ID);
            const tag = allTags.find((t) => t.id === id);

            Transforms.delete(editor, {
                unit: "character",
                distance: slashSearch.length + 1,
                reverse: true,
            });

            insertMention(editor, id, tag?.length ?? 1);
            closeSlashMenu();
            ReactEditor.focus(editor);
        },
        [editor, allTags, slashSearch, closeSlashMenu]
    );

    const slashMenuOpen = !!slashTarget && filteredUsers.length > 0;
    const anchorEl = props.composerAnchorRef?.current ?? editorRef.current;

    const handleEditableKeyDown = (e: React.KeyboardEvent) => {
        if (slashMenuOpen) {
            if (e.key === "ArrowDown") {
                e.preventDefault();
                setHighlightIndex((i) => (i < 0 ? 0 : Math.min(i + 1, filteredUsers.length - 1)));
                return;
            }

            if (e.key === "ArrowUp") {
                e.preventDefault();
                setHighlightIndex((i) => (i <= 0 ? -1 : i - 1));
                return;
            }

            if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                selectUser(filteredUsers[highlightIndex >= 0 ? highlightIndex : 0]);
                return;
            }

            if (e.key === "Escape") {
                e.preventDefault();
                closeSlashMenu();
                return;
            }
        }

        props.onKeyDown(e);
    };

    return (
        <Slate
            editor={editor}
            initialValue={EMPTY_VALUE}
            onChange={(value) => {
                updateSlashMenu();

                props.onChange(
                    { target: { value: mapDescendantsToText(value) || "" } },
                    computeLength(value)
                );
            }}
        >
            <div ref={editorRef}>
                <Editable
                    renderElement={renderElement}
                    onKeyDown={handleEditableKeyDown}
                    style={{
                        lineHeight: props.lineHeight ?? 1.5,
                        overflowWrap: "anywhere",
                    }}
                    placeholder={props.placeholder}
                    tabIndex={11}
                    readOnly={!!props.readOnly}
                    spellCheck
                />
            </div>

            <Popover
                open={slashMenuOpen}
                anchorEl={anchorEl}
                onClose={closeSlashMenu}
                anchorOrigin={{ vertical: "top", horizontal: "left" }}
                transformOrigin={{ vertical: "bottom", horizontal: "left" }}
                disableAutoFocus
                disableRestoreFocus
                disableEnforceFocus
                slotProps={{
                    root: { sx: { pointerEvents: "none" } },
                    paper: {
                        sx: {
                            pointerEvents: "auto",
                            maxWidth: "min(20rem, calc(100vw - 2rem))",
                        },
                    },
                }}
            >
                <div
                    className="slash-user-mention-popover min-w-28 max-w-40 bg-color-internal-note-slash-popover-bg"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={(e) => e.stopPropagation()}
                >
                    <div className="d-flex flex-direction max-h-32 o-y-auto">
                        {filteredUsers.map((user, index) => {
                            const name = slashDisplayName(user);
                            const selected = index === highlightIndex;

                            return (
                                <button
                                    key={user.ID}
                                    type="button"
                                    className={`slash-user-mention-popover__row b-none bg-transparent w-p-100 d-flex align-center c-pointer t-align-left b-none hover-color-internal-note-slash-popover-row-hover${selected ? " bg-color-internal-note-slash-popover-row-hover" : ""
                                        }`}
                                    onClick={() => selectUser(user)}
                                >
                                    <div className="my-auto p-relative flex-shrink-0">
                                        <Avatar
                                            Name={name}
                                            className="avatar my-auto h6-regular"
                                            ImgSrc={
                                                user.ImageReferenceID
                                                    ? `${Config.API_URL}/api/File/${user.ImageReferenceID}/view`
                                                    : ""
                                            }
                                            Size={28}
                                            Index={index}
                                            PhoneNumber={user.PhoneNumber}
                                        />
                                    </div>

                                    <span className="flex-1 min-w-0">
                                        <span className="h5-regular lh-1 color-tags-title">
                                            {name}
                                        </span>
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                </div>
            </Popover>
        </Slate>
    );
}